/**
 * useVendor — fetches the current user's store_vendors row.
 * Returns vendor, loading, error, refetch.
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";

export type VendorRecord = {
  id: string;
  user_id: string;
  display_name: string;
  slug: string;
  bio: string | null;
  logo_url: string | null;
  banner_url: string | null;
  status: string;
  app_id: string | null;
  contact_email: string | null;
  support_email: string | null;
  policy_returns: string | null;
  policy_shipping: string | null;
  stripe_account_id: string | null;
  stripe_charges_enabled: boolean;
  stripe_payouts_enabled: boolean;
  stripe_details_submitted: boolean;
  identity_verified: boolean;
  total_sales_cents: number;
  total_orders: number;
  rating_avg: number | null;
  rating_count: number;
  metadata: Record<string, unknown> | null;
  created_at: string;
};

export const VENDOR_FIELDS =
  "id, user_id, display_name, slug, bio, logo_url, banner_url, status, app_id, contact_email, support_email, policy_returns, policy_shipping, stripe_account_id, stripe_charges_enabled, stripe_payouts_enabled, stripe_details_submitted, identity_verified, total_sales_cents, total_orders, rating_avg, rating_count, metadata, created_at";

export function useVendor() {
  const { user } = useAuth();
  const [vendor, setVendor] = useState<VendorRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!user) {
      setVendor(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: e } = await supabase
      .from("store_vendors")
      .select(VENDOR_FIELDS)
      .eq("user_id", user.id)
      .maybeSingle();
    if (e) setError(e.message);
    setVendor((data as unknown as VendorRecord) ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const isSandbox = !!(vendor?.metadata as Record<string, unknown> | null)?.sandbox;

  return { vendor, loading, error, refetch, isSandbox, setVendor };
}
